import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateProjectCache } from 'src/api/project'
import { invalidateProjectPositionCache, useBulkConfigurePositions } from 'src/api/projectPosition'
import { IPositionType } from 'src/generated-types'
import { ConfigurationCard, IConfigurePositionsData, PositionConfig } from 'src/generated-types/configure-positions'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { isRenameOnlyEdit } from './ConfigurePositionsForm.action'
import { buildExistingGroups, findGroupByName } from './configure/existingGroups.action'

interface UseConfigureAssignmentOptions {
  customerId: EntityId
  projectId: EntityId
  localityId: EntityId
  activeType: IPositionType | null
  cards: ConfigurationCard[]
  configById: Map<number, PositionConfig>
  selectedIds: Set<number>
  drawings: IPositionDrawing[] | undefined
  setTypeSelection: (type: IPositionType, ids: number[]) => void
  removeFromSelection: (ids: number[]) => void
}

export const useConfigureAssignment = ({
  customerId,
  projectId,
  localityId,
  activeType,
  cards,
  configById,
  selectedIds,
  drawings,
  setTypeSelection,
  removeFromSelection,
}: UseConfigureAssignmentOptions) => {
  const { t } = useTranslation(['position', 'common', 'common-api-message'])
  const { showConfirmDialog } = useDialogCtx()
  const { mutateAsync: bulkConfigurePositions, isLoading: isSaving } = useBulkConfigurePositions(customerId, projectId)
  const [pickedDrawingId, setPickedDrawingId] = useState<number | null>(null)
  const [grouping, setGrouping] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null)
  const [editingCardKey, setEditingCardKey] = useState<string | null>(null)

  const existingGroups = useMemo(() => buildExistingGroups(cards), [cards])
  const matchedGroup = grouping ? findGroupByName(existingGroups, groupName) : undefined
  const lockedGroup = matchedGroup && matchedGroup.key !== editingCardKey ? matchedGroup : undefined

  const isRenameOnly = useMemo(
    () =>
      isRenameOnlyEdit({
        editingCard: cards.find(card => card.key === editingCardKey),
        grouping,
        pickedDrawingId,
        selectedIds,
        groupName,
      }),
    [cards, editingCardKey, grouping, pickedDrawingId, selectedIds, groupName],
  )

  const resetAssignment = useCallback(() => {
    setPickedDrawingId(null)
    setGrouping(false)
    setGroupName('')
    setEditingGroupId(null)
    setEditingCardKey(null)
  }, [])

  const changeGroupName = useCallback(
    (value: string) => {
      setGroupName(value)
      const match = findGroupByName(existingGroups, value)
      if (match && match.key !== editingCardKey) setPickedDrawingId(match.drawingId)
    },
    [existingGroups, editingCardKey],
  )

  const changeGrouping = useCallback(
    (value: boolean, suggestedName: string) => {
      setGrouping(value)
      if (value && !groupName.trim()) setGroupName(suggestedName)
    },
    [groupName],
  )

  const pickDrawing = useCallback(
    (drawingId: number) => {
      if (lockedGroup && drawingId !== lockedGroup.drawingId) {
        toast.info(t('configure-group-drawing-locked', { ns: 'position', drawing: lockedGroup.drawingNumber }))
        return
      }
      setPickedDrawingId(current => (current === drawingId ? null : drawingId))
    },
    [lockedGroup, t],
  )

  const persistConfiguration = useCallback(
    async (requests: IConfigurePositionsData[]) => {
      try {
        await bulkConfigurePositions(requests)
        invalidateProjectPositionCache.getProjectPositions(customerId, projectId)
        invalidateProjectPositionCache.getPositionDrawingGroups(customerId, projectId)
        invalidateProjectPositionCache.getPositionsGroupedByDrawingGroups(customerId, projectId)
        invalidateProjectPositionCache.getAllPositionDrawingGroups(customerId, projectId)
        invalidateProjectCache.getProject(customerId, localityId, projectId)
        invalidateProjectPositionCache.getProjectComponentCount(customerId, projectId)
        invalidateProjectCache.getProjectMapSVG(customerId, localityId, projectId)
        invalidateProjectCache.getProjectForMap(customerId, localityId, projectId)
        toast.success(t('positions-configured-successfully', { ns: 'position' }))
        return true
      } catch {
        toast.error(t('unExpectedError', { ns: 'common-api-message' }))
        return false
      }
    },
    [bulkConfigurePositions, customerId, projectId, localityId, t],
  )

  const removeDrawings = useCallback(
    async (ids: number[]) => {
      const configuredIds = ids.filter(id => configById.has(id))
      if (!configuredIds.length || activeType == null) return
      const success = await persistConfiguration([{ positions: configuredIds, drawingId: null, groupId: null }])
      if (!success) return
      removeFromSelection(configuredIds)
      resetAssignment()
    },
    [configById, activeType, persistConfiguration, removeFromSelection, resetAssignment],
  )

  const confirmRemoveDrawings = useCallback(
    (ids: number[]) => {
      const configuredCount = ids.filter(id => configById.has(id)).length
      if (!configuredCount) return
      showConfirmDialog(
        t('remove-drawing', { ns: 'position' }),
        t('configure-confirm-remove', { ns: 'position', count: configuredCount }),
        { acceptText: t('yes', { ns: 'common' }), cancelText: t('no', { ns: 'common' }) },
        async pop => {
          await removeDrawings(ids)
          pop()
        },
      )
    },
    [configById, showConfirmDialog, removeDrawings, t],
  )

  const assign = useCallback(async () => {
    if (activeType == null || !pickedDrawingId || selectedIds.size === 0) {
      toast.error(t('select-a-drawing', { ns: 'position' }))
      return
    }
    const pickedDrawing = drawings?.find(drawing => drawing.id === pickedDrawingId)
    if (pickedDrawing && pickedDrawing.category !== activeType) {
      toast.error(t('selected-drawing-type-mismatch', { ns: 'position' }))
      return
    }
    const trimmedGroupName = groupName.trim()
    if (grouping && !trimmedGroupName) {
      toast.error(t('group-name-required', { ns: 'position' }))
      return
    }
    const groupId = grouping
      ? findGroupByName(existingGroups, trimmedGroupName)?.id ??
        (editingGroupId && editingGroupId > 0 ? editingGroupId : null)
      : null
    const success = await persistConfiguration([
      {
        positions: [...selectedIds],
        drawingId: pickedDrawingId,
        groupId,
        groupName: grouping ? trimmedGroupName : undefined,
      },
    ])
    if (!success) return
    setTypeSelection(activeType, [])
    resetAssignment()
  }, [
    activeType,
    pickedDrawingId,
    selectedIds,
    drawings,
    groupName,
    grouping,
    existingGroups,
    editingGroupId,
    persistConfiguration,
    setTypeSelection,
    resetAssignment,
    t,
  ])

  const editCard = useCallback(
    (card: ConfigurationCard) => {
      if (activeType == null) return
      setTypeSelection(
        activeType,
        card.positions.map(position => position.id),
      )
      setPickedDrawingId(card.drawingId)
      setEditingCardKey(card.key)
      setEditingGroupId(card.groupId)
      setGrouping(!!card.groupName)
      setGroupName(card.groupName ?? '')
    },
    [activeType, setTypeSelection],
  )

  return {
    pickedDrawingId,
    grouping,
    groupName,
    isEditing: editingCardKey != null,
    isRenameOnly,
    isSaving,
    existingGroups,
    lockedGroup,
    resetAssignment,
    changeGroupName,
    changeGrouping,
    pickDrawing,
    assign,
    confirmRemoveDrawings,
    editCard,
  }
}
